import { FC, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useQuery from "../../../../shared/components/hooks/useQuery";
import useMutation from "../../../../shared/components/hooks/useMutation";
import { Service } from "../../../../shared/interfaces/service";
import { useFormik } from "formik";
import { customizeValidationSchema } from "../variables/customSchema";
import { TitleBarProps } from "../../../../shared/components/titleBar";
import { getTextFields } from "../variables/getTextFields";
import { Grid, InputLabel, TextField } from "@mui/material";
import { FormContainer } from "../../../../shared/components/formContainer";
import ToggleSwitch from "../../../../shared/components/switch";
import InputTextfield from "../../../../shared/components/textField";
import SingleEntityImageViewer from "../../../../shared/components/singleEntityImageViewer";
import { ViewContainer } from "../../../../shared/components/viewContainer";
import SingleEntityContainer from "../../../../shared/components/singleEntityContainer";
import { ProfileBorderBox } from "../../../../shared/components/profileComponents";
import { EditButton } from "../../../../shared/components/editButton";
import { CloseButton } from "../../../../shared/components/closeButton";

const ServiceEditView: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { loading, data, fetchData } = useQuery(`/services/${id}`, null);
  const { loading: apiLoading, modifyData } = useMutation();
  const [editStatus, setEditStatus] = useState(false);
  const serviceData: Service = (data as any)?.data || {};
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  //Edit permission checking
  const entityPermissions: any = (data as any)?.entityPermissions;
  const editPermission = entityPermissions
    ? entityPermissions?.includes("Service-Update")
    : true;
  //End of edit permission checking
  const initialValues = {
    name: serviceData?.name,
    description: serviceData?.description,
    enabled: serviceData?.enabled ? true : false,
  };

  const servicesForm = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: customizeValidationSchema(),
    onSubmit: async (formData: any) => {
      let response;
      if (id) {
        const formDataServices = new FormData();
        if (selectedFile) {
          formDataServices.append("file", selectedFile);
        }
        formDataServices.append("name", formData.name);
        formDataServices.append("description", formData.description);
        formDataServices.append("enabled", formData.enabled ? "true" : "false");
        response = await modifyData(
          `/services/${id}`,
          "put",
          formDataServices,
          servicesForm
        );
      }
      if (response) {
        closeUserEdit();
      }
    },
  });

  const closeUserEdit = () => {
    fetchData();
    setEditStatus(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    setSelectedFile(file);

    if (file) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }

    servicesForm.setFieldValue("iconURL", file || "");
  };

  const editTitlebar: TitleBarProps = {
    headerTitle: "Service -  Edit",
    title: "Edit Service",
    buttonTitle: "Save",
    onClick: () => servicesForm.handleSubmit(),
    dirty: servicesForm.dirty,
    buttonLoading: apiLoading,
    editCloseIcon: {
      visible: true,
      onClick: () => closeUserEdit(),
    },
  };

  const viewTitleBar: TitleBarProps = {
    title: "Service Details",
    headerTitle: "Services Details",
  };

  const serviceEditFields = getTextFields(serviceData);

  const closeDetailsView = () => {
    navigate("/services");
  };

  const serviceEditTooltipNode = (
    <>
      {editPermission && <EditButton onClick={() => setEditStatus(true)} />}
      <CloseButton onClick={() => closeDetailsView()} />
    </>
  );

  return (
    <>
      {editStatus && (
        <>
          <ProfileBorderBox>
            <FormContainer titleBar={editTitlebar}>
              <Grid
                container
                alignItems="stretch"
                columnSpacing={4}
                rowSpacing={2}
              >
                <Grid item xs={12}>
                  <ToggleSwitch
                    status={servicesForm?.values?.enabled}
                    handleChange={() =>
                      servicesForm.setFieldValue(
                        "enabled",
                        !servicesForm?.values?.enabled
                      )
                    }
                  />
                </Grid>
                {serviceEditFields.map(
                  (field: any, index: number) =>
                    (field?.formField === undefined ||
                      field.formField === true) && (
                      <Grid item md={6} sm={6} xs={12} key={field.id}>
                        <InputTextfield
                          form={servicesForm}
                          fieldProps={field}
                          key={index}
                        />
                      </Grid>
                    )
                )}
                <Grid item md={6} sm={6} xs={6} key="service-iconURL">
                  <InputLabel>Icon</InputLabel>
                  <TextField
                    size="small"
                    fullWidth
                    id="outlined-basic"
                    type={"file"}
                    sx={{
                      marginTop: "1vh",
                      "& .MuiInputBase-root": {
                        height: "40px",
                        fontSize: "1rem",
                      },
                    }}
                    onChange={handleFileChange}
                  />
                </Grid>
                <Grid item md={3} sm={6} xs={6} key="service-iconPreview">
                  <div>
                    <SingleEntityImageViewer
                      src={previewUrl ? previewUrl : serviceData?.iconUrl}
                      alt="Event"
                    />
                  </div>
                </Grid>
              </Grid>
            </FormContainer>
          </ProfileBorderBox>
        </>
      )}

      {!editStatus && (
        <ProfileBorderBox>
          <ViewContainer loading={loading} titleBar={viewTitleBar}>
            <div>
              <SingleEntityContainer
                entityFields={serviceEditFields}
                entityTitle=""
                tooltips={serviceEditTooltipNode}
              ></SingleEntityContainer>
            </div>
          </ViewContainer>
        </ProfileBorderBox>
      )}
    </>
  );
};

export default ServiceEditView;
